import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { User, Mail, Phone, FileText, DollarSign, Send, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export default function SellToUsPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    item_description: '',
    estimated_value: '',
    images: []
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.item_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/sell-to-us`, formData);
      setSubmitted(true);
      toast.success('Submission received successfully!');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="sell-to-us-success">
        <CheckCircle className="w-20 h-20 text-green-500 mb-6" />
        <h2 className="heading-display text-3xl text-white mb-4 text-center">Submission Received!</h2>
        <p className="text-white/60 text-center max-w-md mb-8">
          Thank you for reaching out! Our team will review your items and contact you within 1-2 business days with an offer.
        </p>
        <Link to="/">
          <Button className="btn-primary">Back to Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="sell-to-us-page">
      {/* Header */}
      <section className="py-16 border-b border-[#27272A] hero-glow">
        <div className="container-main">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-200 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <span className="label-caps text-[#C41E3A] mb-4 block">Got Memorabilia?</span>
          <h1 className="heading-display text-4xl md:text-5xl text-white mb-4" data-testid="sell-to-us-title">
            Sell To Us
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Have authenticated sports memorabilia you're looking to sell? We're always interested in quality items. Get a fair offer within 48 hours.
          </p>
        </div>
      </section>

      {/* What We Buy */}
      <section className="py-12 border-b border-[#27272A] bg-[#0A0A0A]">
        <div className="container-main">
          <h2 className="font-barlow font-bold text-xl text-white mb-6">What We're Looking For</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-[#27272A] p-4 text-center">
              <span className="text-white/70">Signed Jerseys</span>
            </div>
            <div className="border border-[#27272A] p-4 text-center">
              <span className="text-white/70">Signed Helmets</span>
            </div>
            <div className="border border-[#27272A] p-4 text-center">
              <span className="text-white/70">Signed Balls</span>
            </div>
            <div className="border border-[#27272A] p-4 text-center">
              <span className="text-white/70">Signed Photos</span>
            </div>
          </div>
          <p className="text-white/50 text-sm mt-4">
            * We only purchase items with authentication from JSA, Beckett, PSA, or Fanatics.
          </p>
        </div>
      </section>

      {/* Submission Form */}
      <section className="section-gap">
        <div className="container-main max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="sell-to-us-form">
            {/* Name */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Your Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  name="name"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field pl-10"
                  data-testid="sell-name"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label-caps text-white/50 mb-2 block">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    name="email"
                    type="email"
                    placeholder="you@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field pl-10"
                    data-testid="sell-email"
                  />
                </div>
              </div>
              <div>
                <label className="label-caps text-white/50 mb-2 block">Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field pl-10"
                    data-testid="sell-phone"
                  />
                </div>
              </div>
            </div>

            {/* Item Description */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Item Description *</label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-5 h-5 text-white/30" />
                <Textarea
                  name="item_description"
                  placeholder="Describe the item(s) you're looking to sell. Include athlete name, type of item, authentication details, condition, etc."
                  value={formData.item_description}
                  onChange={handleChange}
                  className="input-field pl-10 min-h-[150px] resize-none"
                  data-testid="sell-description"
                />
              </div>
            </div>

            {/* Estimated Value */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Estimated Value (Optional)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  name="estimated_value"
                  placeholder="Your asking price or estimated value"
                  value={formData.estimated_value}
                  onChange={handleChange}
                  className="input-field pl-10"
                  data-testid="sell-value"
                />
              </div>
            </div>

            {/* Info Note */}
            <div className="bg-[#0A0A0A] border border-[#27272A] p-4">
              <p className="text-white/60 text-sm">
                <strong className="text-white">Pro Tip:</strong> Include photos of your items and authentication certificates in your email response for faster processing.
              </p>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="sell-submit"
            >
              {loading ? 'Submitting...' : 'Submit for Review'}
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
